/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef } from 'react';
import { FieldArray, Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import Icon from 'components/Icon';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCollum,
  InlineLabel,
  ViewText,
  ButtonContainer,
  ButtonContent,
  LabelEdit
} from './styles';
import { GridColumnTitle, Label } from 'components/Form';
import Row from 'components/Grid/Row';
import Col from 'components/Grid/Col';
import { FitinsurAPI, MessageCenter } from 'utils';
import Button from 'components/Button';
import { GlobalLoaderContext, GlobalAuthContext } from 'store/context';
import ModalConfirmDelete from 'components/ModalConfirmDelete';
import ComboBox from 'components/Form/ComboBox3';

const AccessGroup = ({ idUser, columns, data, onSelectClient, options, onSaveGroupMembership }) => {
  const formikRef = useRef(null);
  const inputRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);

  const [sortBy, setSortBy] = useState({ key: null, direction: null, });
  const [addAccessGroup, setAddAccessGroup] = useState(false);
  const [idAccessGroup, setIdAccessGroup] = useState(null);
  const [validations, setValidations] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [initial, setInitial] = useState({
    accessGroupClient: '',
    accessGroup: '',
  });

  const handleDeleteAccessGroup = async () => {
    try {
      loading.start();
      if (deleteId) {
        fitinsur.setAuthToken(authContext.token);
        const response = await fitinsur.requestDeleteAccessGroup(deleteId).then(res => res.json())

        if (response?.ret_code !== 200) {
          mc.error(response.ret_msg_details)
          loading.stop();
          return;
        }

        onSaveGroupMembership()
      }

      loading.stop();

    } catch (err) {
      loading.stop();
      console.log(err);
      mc.error("An error happened, please try again.");
    };
  }

  const resetForm = () => {
    formikRef.current.values.accessGroupClient = '';
    formikRef.current.values.accessGroup = '';
  }

  const handleSaveAccessGroup = async () => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSaveAccessGroup({
        idUser: idUser,
        idGroup: formikRef.current.values.accessGroup.idGroup
      })
        .then(res => res.json());

      if (response.ret_code !== 200) {
        console.log('error', response)
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }
      setIdAccessGroup(null);
      setAddAccessGroup(false);
      resetForm();
      onSaveGroupMembership()
      loading.stop();

    } catch (err) {
      loading.stop();
      console.log(err);
      mc.error("An error happened, please try again.");
    };
  }

  const createValidation = () => {
    let validations = yup.object().shape({
      accessGroupClient: yup.string().required("* Required field"),
      accessGroup: yup.string().required("* Required field"),
    });
    setValidations(validations);
    return validations
  }

  useEffect(() => {
    createValidation();
  }, []);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={() => handleSaveAccessGroup()}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <ModalConfirmDelete
            show={deleteModalOpen}
            onModalClose={() => {
              setDeleteModalOpen(false);
              setIdAccessGroup(null);
            }}
            onConfirm={() => handleDeleteAccessGroup()} />

          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) =>
                  <TableHeader
                    right={column.right}
                    key={index}
                    minWidth={column.minWidth}
                    maxWidth={column.maxWidth}
                  >
                    <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                      {column.label}
                    </GridColumnTitle>
                    {column.value && sortBy.key === column.value && sortBy.direction === "asc" ?
                      <Icon name={"arrowup"} /> :
                      column.value && sortBy.key === column.value && sortBy.direction === "desc" ?
                        <Icon name={"arrowdown"} /> :
                        ''}
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <FieldArray
                name="management"
                render={() => data.map((item, index) => (
                  <TableRow key={index} >
                    <TableCollum>
                      <InlineLabel>
                        Client
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.clientName}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Access Group
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.groupName}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum right>
                      <InlineLabel>
                        ACTION
                      </InlineLabel>
                      <Label>
                        <Icon
                          name="trash"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => {
                            setDeleteId(item.idGroupMembership);
                            setDeleteModalOpen(true);
                            resetForm();
                          }}
                        />
                      </Label>
                    </TableCollum>
                  </TableRow>
                ))}
              />
            </TableBody>
          </Table>

          <Row>
            <Col lg={6} md={6} sm={6} xs={12}></Col>
            <Col lg={6} md={6} sm={6} xs={12}>
              <ViewText style={{ marginTop: '15px' }}>
                <Icon name="plus" onClick={() => setAddAccessGroup(true)} style={{ cursor: 'pointer' }} />
              </ViewText>
            </Col>
          </Row>


          {addAccessGroup && <Form>
            <div style={{ marginTop: 40, marginBottom: 10 }}>
              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="accessGroupClient"
                    label="Client"
                    options={options.clients.map(client => ({
                      label: client.personLastName,
                      value: {
                        ...client,
                      }
                    }))}
                    searchList={[]}
                    onChange={(e) => {
                      setFieldValue('accessGroupClient', e)
                      onSelectClient(e.idPersonRelationship)
                    }}
                    component={ComboBox}
                    isRequired
                    value={values.accessGroupClient}
                  />
                </Col>

                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="accessGroup"
                    label="Access Group"
                    options={options.accessGroup
                      .filter(v => v.idGroup !== 25284)
                      .map(group => ({
                      label: group.groupName,
                      value: {
                        ...group,
                      }
                    }))}
                    searchList={[]}
                    onChange={(e) => {
                      setFieldValue('accessGroup', e)
                    }}
                    component={ComboBox}
                    isRequired
                  />
                </Col>
              </Row>

              <ButtonContainer>
                <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }} />
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    third
                    onClick={() => {
                      setIdAccessGroup(null);
                      setAddAccessGroup(false);
                      resetForm();
                    }}
                  >
                    CANCEL
                  </Button>
                </ButtonContent>
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    type="submit"
                    primary
                    onClick={() => {
                      createValidation().validate(values).catch((e) => {
                        console.log('validation error', e)
                        mc.error('Please fill in all required fields.')
                      })
                    }}
                    id="btnSubmit">
                    {idAccessGroup ? "SAVE" : "ADD ACCESS GROUP"}
                  </Button>
                </ButtonContent>
              </ButtonContainer>
            </div>
          </Form>}
        </>
      )}
    </Formik>
  )
}

export default AccessGroup;